import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CryodTimingWaterfall } from "@/components/ci/cryod-timing-waterfall";
import { CiFailureCard } from "@/components/ci/failure-card";
import { FailurePhaseBadge } from "@/components/ci/failure-phase-badge";
import { CiLogViewer } from "@/components/ci/log-viewer";
import { CiStageFlow } from "@/components/ci/stage-flow";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { TestTypeBadge } from "@/components/ci/test-type-badge";
import { TimingWaterfall } from "@/components/ci/timing-waterfall";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCiTestResult } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { formatEngineLabel } from "@/lib/format";
import { cn, formatBytes, formatMs } from "@/lib/utils";

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-mono text-sm">{value}</dd>
    </div>
  );
}

type Tab = "timing" | "logs";

export function CiTestDetailPage() {
  const { runId, testId } = useParams<{ runId: string; testId: string }>();
  const { data: result, isLoading } = useCiTestResult(runId, testId ? Number(testId) : undefined);
  usePageTitle(result ? `${result.test_name} — ${result.version}` : "Test Detail");
  const [activeTab, setActiveTab] = useState<Tab>("timing");
  // A Mooncake suite has no timing breakdown: its page is the logs.
  const isMooncakeSuite = result?.operation === "mctest";
  const current: Tab = isMooncakeSuite ? "logs" : activeTab;
  const tabs: readonly Tab[] = isMooncakeSuite ? ["logs"] : ["timing", "logs"];

  if (isLoading) {
    return (
      <div className="w-full space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">Test result not found</div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      {/* Breadcrumb */}
      <div>
        <div className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/ci" className="hover:text-foreground">
            CI Runs
          </Link>
          <span>/</span>
          <Link to={`/ci/${runId}`} className="hover:text-foreground font-mono">
            #{runId}
          </Link>
          <span>/</span>
          <span className="text-foreground">{result.test_name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold font-mono">{result.test_name}</h1>
          <TestTypeBadge type={result.test_type ?? "criu"} />
          <CiStatusBadge status={result.status} />
          {result.status === "fail" && result.failure_phase && (
            <FailurePhaseBadge phase={result.failure_phase} />
          )}
          <span className="rounded-md border px-2 py-0.5 text-xs font-mono">{result.version}</span>
          {result.engine_version && (
            <span
              className="rounded-md border px-2 py-0.5 text-xs font-mono text-muted-foreground"
              title="Engine version (workload image tag)"
            >
              {formatEngineLabel(result.engine)} {result.engine_version}
            </span>
          )}
          {result.nvidia_driver_version && (
            <span
              className="rounded-md border px-2 py-0.5 text-xs font-mono text-muted-foreground"
              title="NVIDIA driver version"
            >
              driver {result.nvidia_driver_version}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{result.operation}</span>
          {result.started_at && (
            <span className="text-sm text-muted-foreground">
              {new Date(result.started_at).toLocaleString(undefined, {
                timeZone: "UTC",
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              (UTC)
            </span>
          )}
        </div>
        {result.test_type === "cryod" && result.nodes ? (
          <p className="mt-1 text-sm text-muted-foreground font-mono">
            {result.nodes.source && <span>source: {result.nodes.source}</span>}
            {result.nodes.source && result.nodes.restore && <span className="mx-1.5">→</span>}
            {result.nodes.restore && <span>restore: {result.nodes.restore}</span>}
            {result.nodes.multi && result.nodes.multi.length > 1 && (
              <span> ({result.nodes.multi.length} nodes)</span>
            )}
          </p>
        ) : (
          result.machine_name && (
            <p className="mt-1 text-sm text-muted-foreground">
              Machine: {result.machine_name}
              {result.machine_ip && ` (${result.machine_ip})`}
              {result.nvidia_driver_version && ` | Driver: ${result.nvidia_driver_version}`}
            </p>
          )
        )}
      </div>

      {/* Stage flow — structured "what happened" pipeline */}
      <CiStageFlow result={result} />

      {/* Tab switcher */}
      <div className="flex gap-1.5 border-b pb-px">
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setActiveTab(name)}
            className={cn(
              "px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
              current === name
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {name === "timing" ? "Timing" : "Logs"}
          </button>
        ))}
      </div>

      {current === "timing" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* Left: Timing Waterfall — cryod gets its own phase set */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timing Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {result.test_type === "cryod" ? (
                <CryodTimingWaterfall result={result} failurePhase={result.failure_phase} />
              ) : (
                <TimingWaterfall result={result} failurePhase={result.failure_phase} />
              )}
            </CardContent>
          </Card>

          {/* Right: Info cards */}
          <div className="space-y-4">
            {/* Checkpoint info */}
            {(result.cold_start_ms != null ||
              result.criu_dump_ms != null ||
              result.checkpoint_size_bytes != null) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Checkpoint</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-3">
                    {result.cold_start_ms != null && (
                      <InfoItem label="Cold Start" value={formatMs(result.cold_start_ms)} />
                    )}
                    {result.criu_dump_ms != null && (
                      <InfoItem
                        label={result.test_type === "cryod" ? "Checkpoint" : "CRIU Dump"}
                        value={formatMs(result.criu_dump_ms)}
                      />
                    )}
                    {result.checkpoint_size_bytes != null && (
                      <InfoItem
                        label="Checkpoint Size"
                        value={formatBytes(result.checkpoint_size_bytes)}
                      />
                    )}
                    {/* weights === 0 means a non-arena dump where weights are
                        not separable — the split would be misleading, hide it */}
                    {result.checkpoint_size_bytes != null &&
                      result.checkpoint_weights_bytes != null &&
                      result.checkpoint_weights_bytes > 0 && (
                        <>
                          <InfoItem
                            label="Weights"
                            value={formatBytes(result.checkpoint_weights_bytes)}
                          />
                          <InfoItem
                            label="Size excl. Weights"
                            value={formatBytes(
                              result.checkpoint_size_bytes - result.checkpoint_weights_bytes,
                            )}
                          />
                        </>
                      )}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Restore info */}
            {(result.total_restore_ms != null || result.criu_restore_ms != null) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Restore</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-3">
                    {result.total_restore_ms != null && (
                      <InfoItem label="Total Restore" value={formatMs(result.total_restore_ms)} />
                    )}
                    {result.criu_restore_ms != null && (
                      <InfoItem label="CRIU Restore" value={formatMs(result.criu_restore_ms)} />
                    )}
                    {result.http_ready_ms != null && (
                      <InfoItem label="HTTP Ready" value={formatMs(result.http_ready_ms)} />
                    )}
                    {result.wake_up_ms != null && (
                      <InfoItem label="Wake Up" value={formatMs(result.wake_up_ms)} />
                    )}
                    {result.inference_ms != null && (
                      <InfoItem label="Inference" value={formatMs(result.inference_ms)} />
                    )}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Transfer info (cross-restore) */}
            {result.transfer_seconds != null && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Transfer</CardTitle>
                </CardHeader>
                <CardContent>
                  <InfoItem
                    label="Transfer Time"
                    value={`${result.transfer_seconds.toFixed(1)}s`}
                  />
                </CardContent>
              </Card>
            )}

            {/* Cryod-specific */}
            {result.test_type === "cryod" &&
              (result.kubernetes_overhead_ms != null ||
                result.mooncake_transfer_ms != null ||
                result.criu_binary_duration_ms != null ||
                result.restore_method) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Cryod</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-3">
                      {result.restore_method && (
                        <InfoItem label="Method" value={result.restore_method} />
                      )}
                      {result.kubernetes_overhead_ms != null && (
                        <InfoItem
                          label="K8s Overhead"
                          value={formatMs(result.kubernetes_overhead_ms)}
                        />
                      )}
                      {result.mooncake_transfer_ms != null && (
                        <InfoItem label="Mooncake" value={formatMs(result.mooncake_transfer_ms)} />
                      )}
                      {result.criu_binary_duration_ms != null && (
                        <InfoItem
                          label="CRIU binary"
                          value={formatMs(result.criu_binary_duration_ms)}
                        />
                      )}
                    </dl>
                  </CardContent>
                </Card>
              )}

            {/* Cryod: node context (replaces Machine card for K8s tests) */}
            {result.test_type === "cryod" && result.nodes && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Nodes</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-2">
                    {result.nodes.source && <InfoItem label="Source" value={result.nodes.source} />}
                    {result.nodes.restore && (
                      <InfoItem label="Restore" value={result.nodes.restore} />
                    )}
                    {result.nodes.multi && result.nodes.multi.length > 0 && (
                      <InfoItem label="Multi" value={result.nodes.multi.join(", ")} />
                    )}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Machine info (criu only — cryod uses the Nodes card) */}
            {result.test_type !== "cryod" && result.machine_name && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Machine</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-2">
                    <InfoItem label="Name" value={result.machine_name} />
                    {result.machine_ip && <InfoItem label="IP" value={result.machine_ip} />}
                    {result.nvidia_driver_version && (
                      <InfoItem label="Driver" value={result.nvidia_driver_version} />
                    )}
                    {result.gpu_type && <InfoItem label="GPU" value={result.gpu_type} />}
                  </dl>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Failure section — heuristic classification + AI summary + raw error */}
      {result.status === "fail" && (current === "timing" || isMooncakeSuite) && (
        <CiFailureCard result={result} />
      )}

      {/* Logs tab */}
      {current === "logs" && runId && (
        <CiLogViewer runId={runId} testName={result.test_name} version={result.version} />
      )}
    </div>
  );
}
